// lib/help/publicArticles.ts
//
// Lector ÚNICO del centro de ayuda público (/help y el "?" de Sprint 2). Lee los
// índices generados por `npm run help:build` (fuente: content/help), importados
// estáticamente (sin I/O en runtime). Reemplaza al viejo registro hardcodeado
// (System A). Tipado contra la forma de PublicIndexEntry / PublicSearchEntry.
import type { PublicIndexEntry, PublicSearchEntry } from "./buildIndex"
import type { Locale } from "@/lib/i18n/config"
import fullJson from "./public.generated.json"
import searchJson from "./public-search.generated.json"

/** Entrada pública completa (incluye body es/en y related ya resuelto a slugs). */
export type PublicArticle = PublicIndexEntry
/** Entrada slim para la isla de búsqueda del cliente (sin body, con keywords). */
export type PublicSearchArticle = PublicSearchEntry
/** Categorías de content/help que usa el set público (primeros-pasos, pagos, pos). */
export type PublicCategory = PublicIndexEntry["category"]

/** Índice público completo, en orden del corpus. */
export const ALL_PUBLIC = fullJson as unknown as PublicArticle[]

/** Índice slim para búsqueda en el cliente (payload menor, sin body). */
export const ALL_PUBLIC_SEARCH = searchJson as unknown as PublicSearchArticle[]

const BY_SLUG = new Map(ALL_PUBLIC.map((a) => [a.public_slug, a]))

/** Artículo público por su slug plano (la URL /help/<slug>). */
export function getPublicArticle(slug: string): PublicArticle | undefined {
  return BY_SLUG.get(slug)
}

/** Artículos públicos de una categoría content/help, en orden del índice. */
export function byPublicCategory(cat: PublicCategory): PublicArticle[] {
  return ALL_PUBLIC.filter((a) => a.category === cat)
}

/**
 * Artículos públicos (índice slim) de una superficie del producto (pos/kds/...),
 * ordenados por categoría y luego por título en español. Lo consume el panel "?"
 * del POS/KDS para listar la ayuda de esa pantalla.
 */
export function articlesForSurface(surface: string): PublicSearchArticle[] {
  return ALL_PUBLIC_SEARCH.filter((a) => a.surface === surface).sort(
    (a, b) => a.category.localeCompare(b.category) || a.title.es.localeCompare(b.title.es),
  )
}

// ─── Búsqueda pública (isla del hero) ────────────────────────────────────────
// Ranking determinista sobre el índice SLIM: título(10) > resumen(4) > keywords(2),
// en el idioma activo (keywords son bilingües, sin locale). Top 8. Acentos-insensible.

function tokens(s: string): string[] {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
}

/** Búsqueda pública pura sobre el índice slim. Rankea título > resumen > keywords. */
export function searchPublic(
  query: string,
  locale: Locale,
  articles: PublicSearchArticle[] = ALL_PUBLIC_SEARCH,
): PublicSearchArticle[] {
  const terms = tokens(query)
  if (terms.length === 0) return []
  const scored = articles
    .map((a) => {
      const title = new Set(tokens(a.title[locale]))
      const summary = new Set(tokens(a.summary[locale]))
      const keywords = new Set((a.keywords ?? []).flatMap(tokens))
      let score = 0
      for (const term of terms) {
        if (title.has(term)) score += 10
        else if (summary.has(term)) score += 4
        else if (keywords.has(term)) score += 2
      }
      return { a, score }
    })
    .filter((x) => x.score > 0)
  scored.sort((x, y) => y.score - x.score)
  return scored.slice(0, 8).map((x) => x.a)
}
